import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'
import { logger } from '@/utils/logger'

const BUGSNAG_API_KEY = process.env.BUGSNAG_API_KEY || 'cacd2d200c882229cc5822f56bb72b53'

// Prevent multiple initializations
let isInitialized = false

export function initBugsnag() {
  if (isInitialized) {
    return Bugsnag
  }

  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    // Enable source maps
    enabledBreadcrumbTypes: ['navigation', 'request', 'process', 'log', 'user', 'state', 'error', 'manual'],
    // Add release stage for better error filtering
    releaseStage: process.env.NODE_ENV,
    enabledReleaseStages: ['production', 'staging', 'development'],
    // Automatically collect console logs
    enabledErrorTypes: {
      unhandledExceptions: true,
      unhandledRejections: true,
    },
    // Maximum number of breadcrumbs to collect
    maxBreadcrumbs: 40,
    onError: (event) => {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('Bugsnag captured error:', 'Bugsnag', {
          errorClass: event.errors[0]?.errorClass,
          errorMessage: event.errors[0]?.errorMessage,
          severity: event.severity,
          unhandled: event.unhandled,
        })
      }
      return true
    },
  })

  // Initialize performance monitoring
  BugsnagPerformance.start({ 
    apiKey: BUGSNAG_API_KEY,
    // Set automatic tracking options
    enabledReleaseStages: ['production', 'staging', 'development'],
    autoInstrumentNetworkRequests: true,
  })

  if (process.env.NODE_ENV === 'development') {
    logger.debug('Bugsnag initialized successfully', 'Bugsnag')
  }
  isInitialized = true
  return Bugsnag
}

// Update user context when user logs in/out
export function updateBugsnagUser(user: { id: string; email: string; name?: string } | null) {
  if (!isInitialized) {
    return
  }

  if (user) {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Updating Bugsnag user context:', 'Bugsnag', { id: user.id, email: user.email, name: user.name })
    }
    Bugsnag.setUser(user.id, user.email, user.name || undefined)
  } else {
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Clearing Bugsnag user context', 'Bugsnag')
    }
    Bugsnag.setUser()
  }
}

// Utility function to manually notify Bugsnag
export function notifyBugsnag(error: Error, metadata?: Record<string, any>) {
  if (!isInitialized) {
    return
  }

  if (process.env.NODE_ENV === 'development') {
    logger.debug('Manually notifying Bugsnag:', 'Bugsnag', {
      errorName: error.name,
      errorMessage: error.message,
      metadata,
    })
  }

  Bugsnag.notify(error, (event) => {
    if (metadata) {
      event.addMetadata('custom', metadata)
    }
  })
}

// Export Bugsnag instance for direct access if needed
export { Bugsnag }